import { recordNoteSaved } from '@streem/analytics';
import { APIError, StreemAPI } from '@streem/api';
import { invariant } from '@streem/toolbox';
import { BehaviorSubject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { streem } from 'streem-sdk-protobuf';
import { logger } from '../../util/logger';
import { accumulateAndMapWallEvents, filterByProperty, findInGroup } from '../wall/utils';
import { lastCreatedAt } from '../../util/rx/last_created_at';
import { determineInitialMarkdown } from '../../util/determine_initial_markdown';
import { localStorageKeyForRoomNotes } from '../../util/local_storage_key_for_room_notes';
export class NoteController {
    /*
     * This is the field we use to determine what to display when rendering the notes editor during and after a call.
     * The Notes Controller will store this state for the lifespan of the session (Room or Detail), but our notes
     * editor will mount and unmount when the user uses the in call tabs or the minimize feature on our accordion ui component.
     */
    get currentInputText() {
        return this.inputState.currentText;
    }
    constructor(config) {
        this.config = config;
        this.loadedYnSubject = new BehaviorSubject(false);
        this.loadedYn = this.loadedYnSubject.asObservable();
        this.roomNoteErrorSubject = new BehaviorSubject(undefined);
        this.roomNoteError = this.roomNoteErrorSubject.asObservable();
        this.noteFinalizedYnSubject = new BehaviorSubject(undefined);
        this.roomNoteSubject = new BehaviorSubject(undefined);
        // update naming here and return currentTextInEditor from getter
        this.inputState = {
            noteChangedSinceSave: false,
            currentText: '',
        };
        this.needsSavingYnSubject = new BehaviorSubject(false);
        /**
         * Room Note for Notes 2.0 feature
         */
        this.observableNote = this.roomNoteSubject.asObservable();
        this.needsSavingYn = this.needsSavingYnSubject.asObservable();
        this.noteFinalizedYn = this.noteFinalizedYnSubject.asObservable();
        this.initializedYnSubject = new BehaviorSubject(false);
        this.initializedYn = this.initializedYnSubject.asObservable();
        this.onNoteUpdated = (currentNoteText) => {
            this.inputState = {
                noteChangedSinceSave: true,
                currentText: currentNoteText,
            };
            this.needsSavingYnSubject.next(true);
        };
        this.notes = this.config.roomWall.itemsByType.pipe(findInGroup('note'), accumulateAndMapWallEvents('note'));
        this.roomNote = this.notes.pipe(filterByProperty('scope', streem.api.Note.Scope.SCOPE_ROOM), lastCreatedAt(), shareReplay(1));
        const templates = config.context.callSettings?.noteTemplates;
        if (Array.isArray(templates) && templates.length > 0) {
            this.defaultTemplate = templates[0].text;
        }
        this.roomIdStr = this.config.context.roomId.toString();
        this.initializeNotes();
    }
    async initializeNotes() {
        const { successfulYn, note } = await this.getRoomNote();
        if (!successfulYn) {
            return;
        }
        const unsavedNote = this.readAndDeleteUnsavedNotesFromLocalStorage(this.roomIdStr);
        // markdown to display in the editor
        const currentInputText = determineInitialMarkdown(note?.text, this.defaultTemplate, unsavedNote);
        const noteText = note?.text ?? '';
        const unsavedChangesExistYn = noteText !== currentInputText;
        this.inputState = {
            noteChangedSinceSave: unsavedChangesExistYn,
            currentText: currentInputText,
        };
        if (unsavedChangesExistYn) {
            this.needsSavingYnSubject.next(true);
        }
        this.initializedYnSubject.next(true);
    }
    async getRoomNote() {
        this.loadedYnSubject.next(false);
        this.roomNoteErrorSubject.next(undefined);
        this.roomNoteSubject.next(undefined);
        invariant(this.config.context.roomId, 'Expected roomId to be defined when retrieving room note');
        try {
            const roomNoteResponse = await StreemAPI.rooms.getRoomNote(this.roomIdStr);
            this.roomNoteSubject.next(roomNoteResponse.note);
            this.noteFinalizedYnSubject.next(roomNoteResponse.note?.finalizedAt !== undefined);
            logger.info(`Room note retrieved for ${this.roomIdStr}`);
            this.loadedYnSubject.next(true);
            return {
                note: roomNoteResponse.note,
                successfulYn: true,
            };
        }
        catch (error) {
            const errorMessage = `Failed to get room note: ${error.message}`;
            logger.error(errorMessage);
            this.roomNoteErrorSubject.next(errorMessage);
            return {
                note: undefined,
                successfulYn: false,
            };
        }
    }
    getStreemshotNote(id) {
        return this.notes.pipe(filterByProperty('scope', streem.api.Note.Scope.SCOPE_STREEMSHOT), filterByProperty('scopeSid', id.toString()), lastCreatedAt());
    }
    /**
     * Saves a streemshot note during an inflight streem.
     * The roomId used is the room the customer is in currently.
     * @param text The streemshot note
     * @param streemshotId The streemshotId
     */
    async saveStreemshotNote(streemshotId, text) {
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when saving Streemshot note');
        try {
            const data = await StreemAPI.rooms.updateStreemshotNote(this.roomIdStr, streemshotId.toString(), {
                text,
                authorSid: this.config.context.localUserId.toString(),
            });
            logger.info(`Streemshot note saved for ${streemshotId}`);
            recordNoteSaved(text.length, streemshotId.toString());
            return data;
        }
        catch (error) {
            logger.error(`Failed to save Streemshot note for ${streemshotId}`, error.message);
            throw error;
        }
    }
    /**
     * Save a room note during an inflight streem.
     * The roomId used is the room the customer is in currently.
     * @param text The note being saved to the current room
     */
    async saveRoomNote(text) {
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when saving room note');
        try {
            const result = await StreemAPI.rooms.updateRoomNote(this.roomIdStr, {
                text,
                authorSid: this.config.context.localUserId.toString(),
            });
            this.roomNoteSubject.next(result.note);
            this.inputState = {
                noteChangedSinceSave: false,
                currentText: text,
            };
            this.needsSavingYnSubject.next(false);
            logger.info(`Room note updated by ${this.config.context.localUserId}`);
        }
        catch (error) {
            logger.error(`Failed to save Room note`, error.message);
            throw error;
        }
    }
    async finalizeRoomNote(text) {
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when sending note');
        try {
            const result = await StreemAPI.rooms.finalizeRoomNote(this.roomIdStr, {
                text,
            });
            this.noteFinalizedYnSubject.next(true);
            if (result.note?.finalizedAt == null) {
                logger.warn(`finalize room note returns unexpected value: ${JSON.stringify({
                    notePopulated: result.note != null,
                    finalizedAt: result.note?.finalizedAt ?? 'undefined',
                })}`);
            }
            this.inputState = {
                noteChangedSinceSave: false,
                currentText: text,
            };
            this.needsSavingYnSubject.next(false);
            logger.info(`Room note sent by ${this.config.context.localUserId}`);
        }
        catch (error) {
            // 409 indicates that the note was already sent, so update noteFinalizedYnSubject
            if (error instanceof APIError && error.status === 409) {
                logger.info('finalizeRoomNote: received 409 error, treating note as already finalized');
                this.noteFinalizedYnSubject.next(true);
                return;
            }
            logger.error(`Failed to send note`, error);
            throw error;
        }
    }
    get isNeedingSave() {
        return this.needsSavingYnSubject.getValue();
    }
    async cleanupBeforeLeaveRoom() {
        if (this.inputState.noteChangedSinceSave) {
            const localStorageKey = localStorageKeyForRoomNotes(this.roomIdStr);
            logger.info(`Note has changed since save, saving note to localStorage using key: ${localStorageKey}`);
            localStorage.setItem(localStorageKey, this.inputState.currentText);
            this.inputState.noteChangedSinceSave = false;
            this.needsSavingYnSubject.next(false);
        }
    }
    readAndDeleteUnsavedNotesFromLocalStorage(roomId) {
        const localStorageKey = localStorageKeyForRoomNotes(roomId);
        const unsavedNotes = localStorage.getItem(localStorageKey);
        localStorage.removeItem(localStorageKey);
        return unsavedNotes ?? undefined;
    }
}
//# sourceMappingURL=notes_controller.js.map